// @ts-nocheck
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles, overrideSpacingStyles } from "@toolkit/Themes/common/mixins";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { useMemo, useState } from "react";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Play, { PlayOffset } from "@store/Library/Components/Toolkit/Icons/Play";
import { ProductModelFeatureBullets } from "@store/Framework/Types/ApiModels";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsFeatureBulletsStyles {
    container?: InjectableCss;
    listContainer?: InjectableCss;
    listItem?: InjectableCss;
    toggleClickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
}

export const productDetailsFeatureBulletsStyles: ProductDetailsFeatureBulletsStyles = {
    container: {
        css: css`
            ${overrideSpacingStyles(pxToRem(12), pxToRem(24))}
        `,
    },
    listContainer: {
        css: css`
            margin-bottom: ${pxToRem(10)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            list-style: none;
            padding-left: 0;
        `,
    },
    listItem: {
        css: css`
            position: relative;
            padding-left: ${pxToRem(16)} !important;
            margin-bottom: 0 !important;
        `,
    },
    toggleClickable: {
        css: css`
            ${linkActionStyles()}
            text-transform: uppercase;
        `,
    },
    icon: {
        src: Play,
        color: "primary",
        css: css`
            ${iconScalableStyles("0.8em", PlayOffset)}
            position: absolute;
            top: 0.45em;
            transform: none;
            left: 0;
        `,
    },
};

const styles = productDetailsFeatureBulletsStyles;

const ProductDetailsFeatureBullets: React.FC<Props> = ({ product }) => {
    const [expanded, setExpanded] = useState(false);
    const { extensionSencoSettings } = useSettingsCollection();

    const featureBullets = useMemo<ProductModelFeatureBullets[]>(() => {
        try {
            const parsed = JSON.parse(product?.properties?.featureBullets);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }, [product?.properties?.featureBullets]);

    const maximumFeatureBullets = (extensionSencoSettings && extensionSencoSettings.maximumFeatureBullets) ?? 4;

    const bulletsToShow = expanded ? featureBullets : featureBullets.slice(0, maximumFeatureBullets);

    if (!featureBullets.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <UnorderedList {...styles.listContainer}>
                {bulletsToShow.map(bullet => (
                    <ListItem key={bullet.index} {...styles.listItem}>
                        <Icon {...styles.icon} />
                        {bullet.featureBullet}
                    </ListItem>
                ))}
            </UnorderedList>
            {featureBullets.length > maximumFeatureBullets && (
                <Clickable onClick={() => setExpanded(!expanded)} {...styles.toggleClickable}>
                    {translate(expanded ? "Read less" : "Read more")}
                </Clickable>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsFeatureBullets),
    definition: {
        displayName: "Product Feature Bullets",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
